import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { track } from "../lib/analytics";
import { fbTrack } from "../lib/fbpixel";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const lineId = (productId, tierQty, variantLabel) => `${productId}::${variantLabel || ""}::${tierQty}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cl_cart")) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("cl_cart", JSON.stringify(items));
  }, [items]);

  // Add a pack (tierQty vials at packPrice) to the cart.
  const addItem = (product, { tierQty = 1, packPrice, quantity = 1, variantLabel = null, variantImage = null } = {}) => {
    const price = packPrice != null ? packPrice : product.price * tierQty;
    const id = lineId(product.id, tierQty, variantLabel);
    track("add_to_cart", { product_slug: product.slug, product_name: product.name });
    fbTrack("AddToCart", {
      content_ids: [product.slug],
      content_name: product.name,
      content_type: "product",
      value: +(price * quantity).toFixed(2),
      currency: "USD",
    });
    setItems((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        return prev.map((i) => (i.id === id ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, { id, product, tierQty, packPrice: price, quantity, variantLabel, variantImage }];
    });
    setOpen(true);
  };

  const updateQty = (id, quantity) => {
    if (quantity <= 0) return removeItem(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity } : i)));
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.packPrice * i.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, updateQty, removeItem, clearCart, count, subtotal }}
    >
      {children}
    </CartContext.Provider>
  );
};
